<template>
  <main class="csv-page-shell">
    <section class="center-card">
      <header class="head">
        <h2>CSV Helper — Purchase / Net Summary</h2>
        <p class="muted">
          Upload an exchange transaction history CSV. We’ll detect the columns (Operation, Base Asset,
          Realized Amount for Primary Asset, USD value) and produce a per-asset summary.
        </p>
      </header>

      <!-- Quick start -->
      <section v-if="showGuide" class="panel lite" role="region" aria-label="How to use" style="margin-bottom:12px">
        <h3 class="panel-title">Quick start</h3>
        <ol class="compact">
          <li><strong>Drop</strong> your CSV (or choose a file).</li>
          <li><strong>Review</strong> detected columns (Advanced → Choose columns) if needed.</li>
          <li><strong>Analyze</strong> to generate Buy/Sell/Net totals by asset.</li>
          <li><strong>Download</strong> the summary as CSV if you want.</li>
        </ol>
        <div class="actions" style="margin-top:10px">
          <button class="btn btn--ghost btn--sm" @click="dismissGuide">Hide guide</button>
        </div>
      </section>
      <section v-else class="panel lite" role="region" aria-label="Show guide" style="margin-bottom:12px; padding:10px">
        <button class="ghost tiny" @click="showGuide = true">Show quick guide</button>
      </section>

      <!-- Upload panel -->
      <section class="panel">
        <div
          class="dropzone"
          :class="dropzoneClass"
          @dragover.prevent="onDragOver"
          @dragenter.prevent="onDragEnter"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          role="button"
          aria-label="CSV dropzone"
          :aria-invalid="isDragInvalid ? 'true' : 'false'"
        >
          <div class="dz-default">
            <p>Drag & drop a .csv file here</p>
            <p class="muted">or</p>

            <label class="file-btn" for="csvInput">Choose CSV</label>
            <input
              id="csvInput"
              ref="fileInput"
              type="file"
              accept=".csv,text/csv"
              @change="onFileInput"
            />

            <p v-if="filename" class="tiny muted" style="margin-top:10px">
              Loaded: <span class="mono">{{ filename }}</span>
              <span v-if="rawRows.length"> · {{ rawRows.length }} row(s)</span>
            </p>
          </div>

          <div class="dz-overlay" aria-hidden="true">
            <p v-if="isDragInvalid" class="dz-callout dz-invalid">Only .csv files are allowed</p>
            <p v-else class="dz-callout">Release to load {{ hoverFilename || 'CSV' }}</p>
          </div>
        </div>

        <div class="actions" style="margin-top:10px">
          <button class="ghost" @click="toggleAdvancedPanel" :aria-expanded="showAdvanced ? 'true' : 'false'">
            {{ showAdvanced ? 'Hide advanced panel' : 'Show advanced panel' }}
          </button>

          <div class="sep"></div>

       <button class="btn btn--primary" @click="analyze" :disabled="isAnalyzing">
  {{ isAnalyzing ? 'Analyzing…' : 'Analyze' }}
</button>

<button class="btn btn--ghost" @click="resetAll" :disabled="isAnalyzing">
  Clear
</button>
        </div>

        <div v-if="errorMessage" class="error" style="margin-top:10px">{{ errorMessage }}</div>
      </section>
<!-- Manual spreadsheet input (Advanced) -->
<section class="panel lite" v-show="showAdvanced" style="margin-top:12px">
  <h3 class="panel-title">Manual paste / edit (optional)</h3>
  <p class="tiny muted" style="margin:6px 0 10px">
    Paste CSV/TSV directly into this grid instead of uploading a file.
    If the grid contains data, <b>Analyze</b> will use the grid (including your edits).
  </p>

  <SpreadsheetComponentAdvancedB
    ref="outputSpreadsheet"
    button-label="Add 100 Rows"
    :highlight-header="false"
    width="100%"
    height="360px"
  />

  <div class="actions" style="margin-top:10px">
    <button class="btn btn--ghost btn--sm" @click="clearSheet" :disabled="isAnalyzing">
      Clear sheet
    </button>
  </div>
</section>

      <!-- Advanced controls -->
      <section v-if="showAdvanced && headerRowIdx >= 0" class="panel lite">
        <h3 class="panel-title">Column overrides & options</h3>

        <div class="grid-2">
          <div>
            <div class="field">
              <label>Operation (Buy/Sell) column</label>
              <select v-model.number="selectedCols.operation">
                <option :value="-1">— auto —</option>
                <option v-for="(h,i) in headerRaw" :key="'op'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>

            <div class="field">
              <label>Asset column (Base Asset)</label>
              <select v-model.number="selectedCols.asset">
                <option :value="-1">— auto —</option>
                <option v-for="(h,i) in headerRaw" :key="'as'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>

            <div class="field">
              <label>Token amount column (Realized Amount For Primary Asset)</label>
              <select v-model.number="selectedCols.amount">
                <option :value="-1">— auto —</option>
                <option v-for="(h,i) in headerRaw" :key="'am'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>

            <div class="field">
              <label>USD value column (…in USD)</label>
              <select v-model.number="selectedCols.usd">
                <option :value="-1">— none / auto —</option>
                <option v-for="(h,i) in headerRaw" :key="'us'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>

            <div class="field">
              <label>Net mode</label>
              <select v-model="netMode">
                <option value="operation">Use Operation (Buy adds, Sell subtracts)</option>
                <option value="signed_amount">Ignore Operation, sum signed Amount (net only)</option>
                <option value="operation_with_fallback">Use Operation, fallback to amount sign when unknown</option>
              </select>
              <p class="tiny muted" style="margin-top:6px">
                If your file’s “Realized Amount …” column is already signed (+/-), <b>signed_amount</b> is often the cleanest.
              </p>
            </div>

            <div class="field inline">
              <label class="checkbox">
                <input type="checkbox" v-model="ignoreZeroAmounts">
                <span>Ignore zero amount rows</span>
              </label>
            </div>
            <div class="field" v-if="analysis?.profile === 'cryptocom' || (headerNorm || []).includes('txn type')">
  <label>Crypto.com order-event filtering</label>
  <select v-model="cryptoComFilterMode">
    <option value="auto">Auto (commit-only if present, else skip lock/unlock)</option>
    <option value="commit_only">Commit rows only</option>
    <option value="exclude_lock_unlock">Skip lock/unlock rows</option>
    <option value="none">No filtering</option>
  </select>
  <p class="tiny muted" style="margin-top:6px">
    Crypto.com limit orders emit lock/unlock/commit rows. Counting lock/unlock will double-count trades.
  </p>
</div>

          </div>

          <div>
            <p class="muted">Detection scores: header matches + content sampling.</p>
            <table class="mini results" style="width:100%">
              <thead>
                <tr>
                  <th>Col</th>
                  <th>Header</th>
                  <th class="right">Op</th>
                  <th class="right">Asset</th>
                  <th class="right">Amt</th>
                  <th class="right">USD</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(h,i) in headerRaw" :key="'st'+i">
                  <td>C{{ i+1 }}</td>
                  <td>{{ h }}</td>
                  <td class="right">{{ (detectionStats.opScore?.[i] ?? 0).toFixed(2) }}</td>
                  <td class="right">{{ (detectionStats.assetScore?.[i] ?? 0).toFixed(2) }}</td>
                  <td class="right">{{ (detectionStats.amountScore?.[i] ?? 0).toFixed(2) }}</td>
                  <td class="right">{{ (detectionStats.usdScore?.[i] ?? 0).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>

            <div class="actions" style="margin-top:10px">
              <button class="btn btn--primary" @click="analyze" :disabled="!rawRows.length || isAnalyzing">
                Re-analyze
              </button>
              <span class="tiny muted" v-if="analysis">
                Using: op=<b>{{ colName(analysis.columns.operation) }}</b>,
                asset=<b>{{ colName(analysis.columns.asset) }}</b>,
                amt=<b>{{ colName(analysis.columns.amount) }}</b>,
                usd=<b>{{ colName(analysis.columns.usd) }}</b>
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Results -->
      <section v-if="analysis" class="panel">
        <header class="analysis__head" style="margin-bottom:8px">
          <h3>Summary</h3>
          <p class="muted">
            Rows: <b>{{ analysis.rowsScanned }}</b> scanned ·
            <b>{{ analysis.rowsUsed }}</b> used ·
            <b>{{ analysis.rowsSkipped }}</b> skipped
            <span v-if="analysis.unknownOperationRows"> · Unknown operation: <b>{{ analysis.unknownOperationRows }}</b></span>
            <span v-if="analysis.usdMissingRows && analysis.columns.usd >= 0"> · Missing USD: <b>{{ analysis.usdMissingRows }}</b></span>
          </p>
          <p class="tiny muted" v-if="analysis.skipBreakdown">
  Skipped breakdown —
  missing asset: <b>{{ analysis.skipBreakdown.missingAsset }}</b>,
  invalid/blank amount: <b>{{ analysis.skipBreakdown.invalidAmount }}</b>,
  zero amount: <b>{{ analysis.skipBreakdown.zeroAmount }}</b>,
  unknown buy/sell: <b>{{ analysis.skipBreakdown.unknownDir }}</b>
</p>

        </header>

        <div class="actions" style="margin-bottom:10px">
          <button class="btn btn--outline btn--sm" @click="downloadSummaryCsv" :disabled="!analysis.perAsset.length">
            Download summary CSV
          </button>
          <button class="btn btn--ghost btn--sm" @click="downloadExtractedRowsCsv" :disabled="!analysis.extractedRows.length">
            Download extracted rows CSV
          </button>

          <div class="sep"></div>

          <label class="inline">
            Filter asset:
            <input class="input input--sm" v-model="assetFilter" placeholder="e.g. BTC" />
          </label>

          <label class="inline">
            Sort:
            <select v-model="sortKey">
              <option value="buyUsd">Buy USD</option>
              <option value="buyQty">Buy Qty</option>
              <option value="sellUsd">Sell USD</option>
              <option value="sellQty">Sell Qty</option>
              <option value="netUsd">Net USD</option>
              <option value="netQty">Net Qty</option>
              <option value="asset">Asset</option>
            </select>
          </label>

          <label class="inline">
            <select v-model="sortDir">
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </label>
        </div>

        <!-- Totals -->
        <div class="panel lite" style="margin-bottom:10px">
          <div class="summary-grid">
            <div class="summary-row">
              <span class="label">Total buy qty</span>
              <span class="value mono">{{ fmtQty(analysis.totals.buyQty) }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total buy USD</span>
              <span class="value mono">{{ analysis.columns.usd >= 0 ? fmtUsd(analysis.totals.buyUsd) : '—' }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total sell qty</span>
              <span class="value mono">{{ fmtQty(analysis.totals.sellQty) }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total sell USD</span>
              <span class="value mono">{{ analysis.columns.usd >= 0 ? fmtUsd(analysis.totals.sellUsd) : '—' }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Net qty</span>
              <span class="value mono">{{ fmtQty(analysis.totals.netQty) }}</span>
            </div>
            <div class="summary-row">
              <span class="label">Net USD</span>
              <span class="value mono">{{ analysis.columns.usd >= 0 ? fmtUsd(analysis.totals.netUsd) : '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Per-asset table -->
        <div class="results-wrap">
          <table class="results" style="width:100%">
            <thead>
              <tr>
                <th>Asset</th>
                <th class="right">Buy qty</th>
                <th class="right">Buy USD</th>
                <th class="right">Buy #</th>
                <th class="right">Avg buy</th>
                <th class="right">Sell qty</th>
                <th class="right">Sell USD</th>
                <th class="right">Sell #</th>
                <th class="right">Avg sell</th>
                <th class="right">Net qty</th>
                <th class="right">Net USD</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sortedFilteredAssets" :key="'a'+row.asset">
                <td class="mono">{{ row.asset }}</td>

                <td class="right mono">{{ fmtQty(row.buyQty) }}</td>
                <td class="right mono">{{ analysis.columns.usd >= 0 ? fmtUsd(row.buyUsd) : '—' }}</td>
                <td class="right mono">{{ row.buyCount }}</td>
                <td class="right mono">{{ analysis.columns.usd >= 0 ? fmtUsd(row.avgBuyPrice ?? null, true) : '—' }}</td>

                <td class="right mono">{{ fmtQty(row.sellQty) }}</td>
                <td class="right mono">{{ analysis.columns.usd >= 0 ? fmtUsd(row.sellUsd) : '—' }}</td>
                <td class="right mono">{{ row.sellCount }}</td>
                <td class="right mono">{{ analysis.columns.usd >= 0 ? fmtUsd(row.avgSellPrice ?? null, true) : '—' }}</td>

                <td class="right mono">{{ fmtQty(row.netQty) }}</td>
                <td class="right mono">{{ analysis.columns.usd >= 0 ? fmtUsd(row.netUsd) : '—' }}</td>
              </tr>

              <tr v-if="sortedFilteredAssets.length === 0">
                <td colspan="11" class="muted">No assets match the current filter.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Debug samples -->
        <details class="panel lite" style="margin-top:12px" v-if="analysis.extractedRows.length">
          <summary>Show extracted row samples (first {{ Math.min(50, analysis.extractedRows.length) }})</summary>
          <table class="results mini" style="width:100%; margin-top:10px">
            <thead>
              <tr>
                <th style="width:90px">Row</th>
                <th>Operation</th>
                <th>Asset</th>
                <th class="right">Amount</th>
                <th class="right">USD</th>
                <th class="muted">Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in analysis.extractedRows.slice(0,50)" :key="'x'+idx">
                <td class="mono">#{{ r.rowIndex + 1 }}</td>
                <td>{{ r.operation ?? '—' }}</td>
                <td class="mono">{{ r.asset ?? '—' }}</td>
                <td class="right mono">{{ r.amountRaw ?? '—' }}</td>
                <td class="right mono">{{ r.usdRaw ?? '—' }}</td>
                <td class="muted">{{ r.note || '' }}</td>
              </tr>
            </tbody>
          </table>
        </details>
      </section>
    </section>
  </main>
</template>

<script>
import Papa from "papaparse";
import SpreadsheetComponentAdvancedB from "@/components/SpreadsheetComponentAdvancedB.vue";

/**
 * Header synonyms you can expand quickly.
 * Everything is compared on a normalized header string.
 */
const HEADER_RULES = {
  operation: [
    { re: /\boperation\b/, w: 18 },
    { re: /\btransaction type\b/, w: 26 },   // COINBASE
    { re: /\btype\b/, w: 8 },                // bumped slightly, still not huge
    { re: /\bbuy\/sell\b/, w: 18 },
    { re: /\bside\b/, w: 14 },
    { re: /\btrade side\b/, w: 16 },
    { re: /\border side\b/, w: 14 },
    { re: /\bdirection\b/, w: 12 },
    { re: /\baction\b/, w: 10 },
  ],

  asset: [
    { re: /^\s*asset\s*$/, w: 30 },          // COINBASE exact header "Asset"
    { re: /\bbase asset\b/, w: 22 },
    { re: /\bprimary asset\b/, w: 18 },
    { re: /\bquote asset\b/, w: 18 },
    { re: /\basset\b/, w: 12 },
    { re: /\bsymbol\b/, w: 10 },
    { re: /\btoken\b/, w: 8 },
    // NOTE: I'd avoid boosting "currency" here because "Price Currency" can steal the pick.
  ],

  amount: [
    { re: /\bquantity transacted\b/, w: 30 }, // COINBASE
    { re: /\brealized amount for primary asset\b/, w: 26 },
    { re: /\brealized amount for base asset\b/, w: 26 },
    { re: /\brealized amount for quote asset\b/, w: 26 },
    { re: /\brealized amount for fee asset\b/, w: 26 },
    { re: /\brealized amount\b/, w: 18 },
    { re: /\bquantity\b/, w: 12 },
    { re: /\bqty\b/, w: 12 },
    { re: /\bamount\b/, w: 8 },
  ],

  usd: [
    { re: /\bsubtotal\b/, w: 30 },           // COINBASE (this is your USD valuation)
    { re: /\btotal\b/, w: 16 },              // fallback
    { re: /\bfees?\b|\bspread\b/, w: 2 },    // low; we mainly handle fees via penalties below

    { re: /\brealized amount for primary asset.*\busd\b/, w: 26 },
    { re: /\brealized amount for base asset.*\busd\b/, w: 26 },
    { re: /\brealized amount for quote asset.*\busd\b/, w: 26 },
    { re: /\brealized amount for fee asset.*\busd\b/, w: 26 },
    { re: /\busd\b/, w: 18 },
    { re: /\bin usd\b/, w: 14 },
    { re: /\b\$\b/, w: 12 },
  ],
};


function normalizeHeader(h) {
  return String(h ?? "")
    .replace(/^\uFEFF/, "") // BOM
    .trim()
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[^a-z0-9$]+/g, " ")
    .trim();
}

function isLikelyBinary(sampleText) {
  // quick heuristic: lots of non-text bytes or NULLs
  if (!sampleText) return false;
  if (sampleText.includes("\0")) return true;
  // many replacement chars means bad decode
  const rep = (sampleText.match(/\uFFFD/g) || []).length;
  return rep > 2;
}

function isCsvLikeFile(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  return name.endsWith(".csv") || type.includes("csv") || type.includes("text/plain");
}

function hasCsvInDataTransfer(dt) {
  if (!dt) return false;
  const items = dt.items;
  if (items && items.length) {
    for (const it of items) {
      if (it.kind === "file") {
        const type = String(it.type || "").toLowerCase();
        if (type.includes("csv") || type.includes("text/plain")) return true;
      }
    }
  }
  const files = dt.files;
  if (files && files.length) {
    const f = files[0];
    return isCsvLikeFile(f);
  }
  return false;
}

function sanitizeCell(value, opts) {
  const {
    trimWhitespace = true,
    neutralizeFormulas = true,
    MAX_CELL_CHARS = 10_000,
  } = opts || {};

  let s = (value == null) ? "" : String(value);

  // Normalize line breaks
  s = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  if (trimWhitespace) s = s.trim();
  if (s.length > MAX_CELL_CHARS) s = s.slice(0, MAX_CELL_CHARS);

  // Neutralize only when NOT numeric (prevents breaking +1.23)
  if (neutralizeFormulas) {
    const t = s.trimStart();
    const isNumeric = /^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(t);
    if (!isNumeric) {
      if (/^[=+@]/.test(t)) s = "'" + s;
      if (/^-/.test(t) && !/^-?\d/.test(t)) s = "'" + s;
    }
  }
  return s;
}


function parseNumber(v) {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;

  // Remove currency symbols and common wrappers
  s = s.replace(/[$€£¥]/g, "");
// Remove common currency codes (optional robustness)
s = s.replace(/\bUSD\b/gi, "");

  // Parentheses => negative
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    neg = true;
    s = s.slice(1, -1).trim();
  }

  // Remove separators/spaces
  s = s.replace(/,/g, "").replace(/\s+/g, "");

  // Grab leading numeric portion (supports exponent)
  const m = s.match(/^[-+]?\d*\.?\d+(e[-+]?\d+)?/i);
  if (!m) return null;

  const num = Number(m[0]);
  if (!Number.isFinite(num)) return null;
  return neg ? -Math.abs(num) : num;
}

function parseOperation(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;

  if (/\bbuy\b/.test(s)) return "buy";
  if (/\bpurchase\b/.test(s)) return "buy";   // ✅ crypto.com
  if (/\bsell\b/.test(s)) return "sell";

  // Some exports use B/S
  if (s === "b") return "buy";
  if (s === "s") return "sell";
  return null;
}
function findCol(headerNorm, patterns) {
  for (let i = 0; i < headerNorm.length; i++) {
    const h = headerNorm[i] || "";
    for (const re of patterns) {
      if (re.test(h)) return i;
    }
  }
  return -1;
}

function detectCryptoComCols(headerNorm) {
  return {
    txnType:     findCol(headerNorm, [/^txn type$/, /^transaction type$/]),
    currency:    findCol(headerNorm, [/^currency$/]),
    amount:      findCol(headerNorm, [/^amount$/]),
    toCurrency:  findCol(headerNorm, [/^to currency$/]),
    toAmount:    findCol(headerNorm, [/^to amount$/]),
    nativeUsd:   findCol(headerNorm, [/^native amount in usd$/]), // normalizeHeader turns "(in USD)" -> "in usd"
    kind:        findCol(headerNorm, [/^transaction kind$/]),
    hash:        findCol(headerNorm, [/^transaction hash$/, /^tx hash$/, /\bhash\b/]),
    desc:        findCol(headerNorm, [/^transaction description$/, /^description$/]),
  };
}

function isCryptoComFile(cc) {
  return (
    cc.txnType >= 0 &&
    cc.currency >= 0 &&
    cc.amount >= 0 &&
    cc.toCurrency >= 0 &&
    cc.toAmount >= 0 &&
    cc.nativeUsd >= 0
  );
}
function deriveCryptoComTradeRow(row, ccCols, opts) {
  const {
    filterMode = "auto",       // "auto" | "commit_only" | "exclude_lock_unlock" | "none"
    hasCommit = false,         // from scanCryptoComKindFlags
  } = opts || {};

  const kindRaw = ccCols.kind >= 0 ? row[ccCols.kind] : "";
  const kind = String(kindRaw ?? "").trim().toLowerCase();

  const txnTypeRaw = ccCols.txnType >= 0 ? row[ccCols.txnType] : "";
  let op = parseOperation(txnTypeRaw);

  // Fallback: infer from kind if needed
  if (!op) {
    if (kind.includes("purchase")) op = "buy";
    else if (kind.includes("sell")) op = "sell";
  }

  // Not a buy/sell row → skip
  if (!op) {
    return { skip: true, skipCode: "nonTrade", note: "Skipped: not a buy/sell/purchase row" };
  }

  // Decide applied filter
  let applied = filterMode;
  if (applied === "auto") {
    if (!kind) applied = "none";
    else if (hasCommit) applied = "commit_only";
    else applied = "exclude_lock_unlock";
  }

  // Apply filter
  if (applied === "commit_only") {
    if (!kind.includes("commit")) {
      return { skip: true, skipCode: "filteredKind", note: "Skipped: not a commit row" };
    }
  } else if (applied === "exclude_lock_unlock") {
    if (kind.includes("lock") || kind.includes("unlock")) {
      return { skip: true, skipCode: "filteredKind", note: "Skipped: lock/unlock bookkeeping row" };
    }
  }

  // (Optional) skip cancels etc if they exist in your exports
  if (/\bcancel|revert|reject|expire|rollback\b/.test(kind)) {
    return { skip: true, skipCode: "filteredKind", note: "Skipped: non-executed order event" };
  }

  const fromCur = String(row[ccCols.currency] ?? "").trim().toUpperCase();
  const toCur   = String(row[ccCols.toCurrency] ?? "").trim().toUpperCase();

  const fromAmtRaw = row[ccCols.amount];
  const toAmtRaw   = row[ccCols.toAmount];

  const fromAmt = parseNumber(fromAmtRaw);
  const toAmt   = parseNumber(toAmtRaw);

  let asset = "";
  let qtySigned = null;
  let qtyRaw = "";

  if (op === "buy") {
    asset = toCur;
    qtySigned = toAmt;
    qtyRaw = toAmtRaw;
    if (qtySigned != null) qtySigned = Math.abs(qtySigned);
  } else if (op === "sell") {
    asset = fromCur;
    qtySigned = fromAmt;
    qtyRaw = fromAmtRaw;
    if (qtySigned != null) qtySigned = -Math.abs(qtySigned);
  }

  if (!asset || qtySigned == null) {
    return { skip: true, skipCode: "missingLeg", note: "Skipped: missing Currency/To Currency or Amount/To Amount" };
  }

  const usdRaw = row[ccCols.nativeUsd];
  const usdVal = parseNumber(usdRaw);

  return {
    skip: false,
    operation: op,
    asset,
    amount: qtySigned,              // signed token qty (+buy, -sell)
    amountRaw: String(qtyRaw ?? ""),
    usd: usdVal,
    usdRaw: String(usdRaw ?? ""),
    kindRaw: String(kindRaw ?? ""),
    txnTypeRaw: String(txnTypeRaw ?? ""),
  };
}

function scanCryptoComKindFlags(rows, startRow, kindCol) {
  let hasCommit = false;
  let hasLockUnlock = false;

  const end = Math.min(rows.length, startRow + 500);
  for (let r = startRow; r < end; r++) {
    const kind = String(rows[r]?.[kindCol] ?? "").toLowerCase();
    if (!kind) continue;
    if (kind.includes("commit")) hasCommit = true;
    if (kind.includes("lock") || kind.includes("unlock")) hasLockUnlock = true;
    if (hasCommit && hasLockUnlock) break;
  }

  return { hasCommit, hasLockUnlock };
}


function looksLikeAssetSymbol(v) {
  const s = String(v ?? "").trim();
  if (!s) return false;
  // Common exchange symbols: BTC, ETH, SOL, PEPE, USDT, etc.
  // Allow digits (e.g., "1INCH") and dashes/underscores rarely used, but keep conservative.
  return /^[A-Za-z0-9]{2,15}$/.test(s) && !/^\d+$/.test(s);
}

function headerScore(headerNorm, rules) {
  let score = 0;
  for (const r of rules) {
    if (r.re.test(headerNorm)) score = Math.max(score, r.w);
  }
  return score;
}


function findHeaderRow(rows) {
  const maxScan = Math.min(rows.length, 30);
  let best = { idx: 0, score: -1 };

const expected = [
  "operation",
  "transaction type",     // coinbase
  "txn type",             // ✅ crypto.com
  "asset",
  "currency",             // ✅ crypto.com
  "to currency",          // ✅ crypto.com
  "quantity transacted",  // coinbase
  "amount",               // ✅ crypto.com
  "to amount",            // ✅ crypto.com
  "subtotal",             // coinbase
  "native amount in usd", // ✅ crypto.com (normalized)
  "realized amount",
  "usd",
  "transaction kind",     // ✅ crypto.com
  "transaction hash"      // ✅ crypto.com
];


  for (let i = 0; i < maxScan; i++) {
    const row = rows[i] || [];
    const normCells = row.map(c => normalizeHeader(c));
    const nonEmpty = normCells.filter(Boolean).length;

    let hits = 0;
    for (const c of normCells) {
      for (const kw of expected) {
        if (c && c.includes(kw)) hits++;
      }
    }

    // Strongly prefer rows that have header-like keywords
    const score = hits * 100 + nonEmpty;
    if (score > best.score) best = { idx: i, score };
  }

  return best.idx;
}

function detectColumns(rows, headerRowIdx, headerNorm) {
  const colCount = headerNorm.length;
  const sample = rows.slice(headerRowIdx + 1, Math.min(rows.length, headerRowIdx + 1 + 400));

  const stats = {
    opScore: new Array(colCount).fill(0),
    assetScore: new Array(colCount).fill(0),
    amountScore: new Array(colCount).fill(0),
    usdScore: new Array(colCount).fill(0),
  };

  for (let c = 0; c < colCount; c++) {
    const h = headerNorm[c] || "";
// Penalties to avoid wrong columns
if (/\bfee\b|\bcommission\b|\bspread\b/.test(h)) {
  stats.amountScore[c] -= 12;
  stats.usdScore[c] -= 14;
}

// Coinbase / generic: avoid selecting price columns as USD valuation
if (/\bprice at transaction\b|\bprice currency\b|\bprice\b/.test(h)) {
  stats.usdScore[c] -= 25;
  stats.amountScore[c] -= 10;
}

// Also avoid “ID” columns, they can be non-empty and distort fillRatio
if (/^\s*id\s*$/.test(h) || /\btransaction id\b/.test(h) || /\border id\b/.test(h)) {
  stats.opScore[c] -= 10;
  stats.assetScore[c] -= 10;
  stats.amountScore[c] -= 10;
  stats.usdScore[c] -= 10;
}

    // Header-based scoring
    stats.opScore[c] += headerScore(h, HEADER_RULES.operation);
    stats.assetScore[c] += headerScore(h, HEADER_RULES.asset);
    stats.amountScore[c] += headerScore(h, HEADER_RULES.amount);
    stats.usdScore[c] += headerScore(h, HEADER_RULES.usd);

    // Penalties to avoid wrong “amount” selection
    if (/\bfee\b|\bcommission\b/.test(h)) {
      stats.amountScore[c] -= 10;
      stats.usdScore[c] -= 6;
    }
    if (/\busd\b|\bdollar\b|\busdt\b/.test(h)) {
      // likely USD valuation column (not token amount)
      stats.amountScore[c] -= 14;
      stats.usdScore[c] += 6;
    }

    // Content sampling
    let nonEmpty = 0, opHits = 0, symHits = 0, numHits = 0;
    for (const row of sample) {
      const v = row?.[c];
      if (v == null) continue;
      const s = String(v).trim();
      if (!s) continue;
      nonEmpty++;
      if (parseOperation(s)) opHits++;
      if (looksLikeAssetSymbol(s)) symHits++;
      if (parseNumber(s) != null) numHits++;
    }
const rowCount = sample.length || 1;
const fillRatio = nonEmpty / rowCount;

// Encourage filled columns a bit
stats.amountScore[c] += fillRatio * 6;
stats.usdScore[c] += fillRatio * 4;
stats.assetScore[c] += fillRatio * 3;
stats.opScore[c] += fillRatio * 3;

// HARD penalty for columns that are essentially empty in the sample
// (this prevents “Primary Asset Amount” from winning when it’s NULL everywhere)
if (rowCount >= 50 && nonEmpty < Math.max(2, Math.floor(rowCount * 0.01))) {
  stats.amountScore[c] -= 40;
  stats.usdScore[c] -= 40;
}

    const denom = Math.max(1, nonEmpty);
    const opRatio = opHits / denom;
    const symRatio = symHits / denom;
    const numRatio = numHits / denom;

    stats.opScore[c] += opRatio * 14;
    stats.assetScore[c] += symRatio * 12;
    stats.amountScore[c] += numRatio * 10;
    stats.usdScore[c] += numRatio * 6;

    // If header explicitly says USD, push it harder to USD column
    if (/\busd\b/.test(h)) stats.usdScore[c] += 10;
  }

  const pickBest = (arr, { exclude = new Set(), minScore = -Infinity } = {}) => {
    let bestIdx = -1;
    let bestScore = minScore;
    for (let i = 0; i < arr.length; i++) {
      if (exclude.has(i)) continue;
      if (arr[i] > bestScore) {
        bestScore = arr[i];
        bestIdx = i;
      }
    }
    return bestIdx;
  };

  // Prefer to pick usd first so we can avoid it for amount
  const usd = pickBest(stats.usdScore, { minScore: 6 });
  const excludeForAmount = new Set();
  if (usd >= 0) excludeForAmount.add(usd);

  const operation = pickBest(stats.opScore, { minScore: 6 });
  const asset = pickBest(stats.assetScore, { minScore: 6 });
  const amount = pickBest(stats.amountScore, { exclude: excludeForAmount, minScore: 6 });

  return { operation, asset, amount, usd, _stats: stats };
}

function toCsvCell(value) {
  if (value == null) return "";
  let s = String(value);

  // Neutralize formula injection for non-numeric strings
  const t = s.trimStart();
  const isNumeric = /^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(t);
  if (!isNumeric) {
    if (/^[=+@]/.test(t)) s = "'" + s;
    if (/^-/.test(t) && !/^-?\d/.test(t)) s = "'" + s;
  }

  // Standard CSV escaping
  if (/[,"\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [];
  lines.push(headers.map(toCsvCell).join(","));
  for (const r of rows) {
    lines.push(r.map(toCsvCell).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function detectMaxCols(rows) {
  let max = 0;
  for (let i = 0; i < rows.length; i++) {
    if (Array.isArray(rows[i]) && rows[i].length > max) max = rows[i].length;
  }
  return max;
}

function trimEmptyEdges(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];

  // Trim trailing empty rows
  let lastNonEmptyRow = -1;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r] || [];
    const hasValue = row.some(v => String(v ?? "").trim() !== "");
    if (hasValue) lastNonEmptyRow = r;
  }
  if (lastNonEmptyRow < 0) return [];
  rows = rows.slice(0, lastNonEmptyRow + 1);

  // Trim trailing empty columns
  const cols = rows.reduce((m, r) => Math.max(m, (r || []).length), 0);
  let lastNonEmptyCol = -1;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows.length; r++) {
      const v = (rows[r] || [])[c];
      if (String(v ?? "").trim() !== "") { lastNonEmptyCol = c; break; }
    }
  }
  const cap = lastNonEmptyCol >= 0 ? lastNonEmptyCol + 1 : 0;
  if (cap <= 0) return [];
  return rows.map(r => (r || []).slice(0, cap));
}

function maybeExplodeSingleCellCsv(rows, Papa) {
  try {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const firstRow = rows[0] || [];
    if (rows.length === 1 && firstRow.length === 1) {
      const cell = firstRow[0];
      if (typeof cell === "string" && /[\n\r]|,|\t|;/.test(cell)) {
        const parsed = Papa.parse(cell, {
          header: false,
          dynamicTyping: false,
          skipEmptyLines: "greedy",
        });
        if (parsed && Array.isArray(parsed.data) && parsed.data.length) {
          return parsed.data;
        }
      }
    }
  } catch (_) { /* ignore */ }
  return rows;
}

export default {
  name: "TxnPurchaseSummary",
   components: { SpreadsheetComponentAdvancedB },
  data() {
    return {
      // UI
      showGuide: true,
      showAdvanced: false,
      errorMessage: "",
      isAnalyzing: false,
cryptoComFilterMode: "auto", // "auto" | "commit_only" | "exclude_lock_unlock" | "none"

      // Drag state
      isDragOver: false,
      isDragInvalid: false,
      dragDepth: 0,
      hoverFilename: "",

      // Limits
      MAX_BYTES: 8 * 1024 * 1024,  // bump to 8MB by default (change if needed)
      MAX_ROWS: 25000,
      MAX_COLS: 300,

      trimWhitespace: true,
      neutralizeFormulas: true,
      MAX_CELL_CHARS: 10_000,
      // Loaded data
      filename: "",
      rawRows: [],
      headerRowIdx: -1,
      headerRaw: [],
      headerNorm: [],

      // Column choices (+ detection scores)
      detectionStats: { opScore: [], assetScore: [], amountScore: [], usdScore: [] },
      selectedCols: { operation: -1, asset: -1, amount: -1, usd: -1 },

      // Analysis options
      netMode: "operation", // 'operation' | 'signed_amount' | 'operation_with_fallback'
      ignoreZeroAmounts: true,

      // Results state
      analysis: null,

      // table UX
      assetFilter: "",
      sortKey: "buyUsd",
      sortDir: "desc",
    };
  },
  computed: {
    dropzoneClass() {
      return {
        "is-over": this.isDragOver,
        "is-invalid": this.isDragInvalid,
      };
    },
    sortedFilteredAssets() {
      if (!this.analysis?.perAsset?.length) return [];
      const q = String(this.assetFilter || "").trim().toUpperCase();

      let arr = this.analysis.perAsset;
      if (q) arr = arr.filter(x => String(x.asset || "").toUpperCase().includes(q));

      const dir = this.sortDir === "asc" ? 1 : -1;
      const key = this.sortKey;

      const getVal = (o) => {
        if (key === "asset") return String(o.asset || "");
        return (o[key] ?? 0);
      };

      return [...arr].sort((a, b) => {
        const av = getVal(a);
        const bv = getVal(b);
        if (typeof av === "string" || typeof bv === "string") {
          return String(av).localeCompare(String(bv)) * dir;
        }
        return (Number(av) - Number(bv)) * dir;
      });
    },
  },
  created() {
    try {
      const hidden = localStorage.getItem("csvhelper.purchase.guide.hidden");
      if (hidden === "1") this.showGuide = false;
    } catch (e) { /* ignore */ }
  },
  methods: {
    dismissGuide() {
      this.showGuide = false;
      try { localStorage.setItem("csvhelper.purchase.guide.hidden", "1"); } catch (e) { /* ignore */ }
    },
    pushIntoSpreadsheet(rows) {
  const comp = this.$refs.outputSpreadsheet;
  if (comp && typeof comp.setData === "function") {
    comp.setData(rows);
  }
},

clearSheet() {
  // Clear just the grid (and analysis), keep advanced toggle state
  this.pushIntoSpreadsheet([[]]);
  this.analysis = null;
  this.errorMessage = "";
  this.headerRowIdx = -1;
  this.headerRaw = [];
  this.headerNorm = [];
  this.detectionStats = { opScore: [], assetScore: [], amountScore: [], usdScore: [] };
  this.selectedCols = { operation: -1, asset: -1, amount: -1, usd: -1 };
},

prepareFromSheet() {
  const comp = this.$refs.outputSpreadsheet;
  if (!comp || typeof comp.getData !== "function") return null;

  let rows = comp.getData() || [];

  // Trim first so a “paste into A1 only” becomes 1x1 and can be exploded
  rows = trimEmptyEdges(rows);

  // If user pasted the entire CSV into one cell, explode it
  rows = maybeExplodeSingleCellCsv(rows, Papa);

  // Trim again after explode
  rows = trimEmptyEdges(rows);

  if (!rows.length) return [];

  // Clamp to MAX_ROWS / MAX_COLS
  const cappedRows = rows.slice(0, this.MAX_ROWS);
  const maxCols = Math.min(this.MAX_COLS, detectMaxCols(cappedRows));

  const cleaned = cappedRows.map(r =>
    (r || []).slice(0, maxCols).map(v =>
      sanitizeCell(v, {
        trimWhitespace: this.trimWhitespace,
        neutralizeFormulas: this.neutralizeFormulas,
        MAX_CELL_CHARS: this.MAX_CELL_CHARS,
      })
    )
  );

  // Push normalized data back into the grid so user sees what we analyze
  this.pushIntoSpreadsheet(cleaned);

  return cleaned;
},

    toggleAdvancedPanel() {
      this.showAdvanced = !this.showAdvanced;
    },

    onFileInput(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.handleFile(file);
      e.target.value = "";
    },
    onDragEnter(e) {
      this.dragDepth++;
      const dt = e.dataTransfer;
      const csvOk = hasCsvInDataTransfer(dt);
      this.isDragOver = true;
      this.isDragInvalid = !csvOk;
      const f = dt && dt.files && dt.files[0];
      this.hoverFilename = csvOk && f && f.name ? f.name : "";
    },
    onDragOver(e) {
      const dt = e.dataTransfer;
      const csvOk = hasCsvInDataTransfer(dt);
      this.isDragOver = true;
      this.isDragInvalid = !csvOk;
      if (dt) dt.dropEffect = csvOk ? "copy" : "none";
      const f = dt && dt.files && dt.files[0];
      this.hoverFilename = csvOk && f && f.name ? f.name : "";
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.resetDragState();
    },
    onDrop(e) {
      const dt = e.dataTransfer;
      const csvOk = hasCsvInDataTransfer(dt);
      const file = dt && dt.files && dt.files[0];
      this.resetDragState();
      if (!csvOk || !file) {
        this.errorMessage = "Only plain-text CSV files are allowed.";
        return;
      }
      this.handleFile(file);
    },
    resetDragState() {
      this.isDragOver = false;
      this.isDragInvalid = false;
      this.dragDepth = 0;
      this.hoverFilename = "";
    },

    resetAll() {
      this.errorMessage = "";
      this.isAnalyzing = false;
      this.filename = "";
      this.rawRows = [];
      this.headerRowIdx = -1;
      this.headerRaw = [];
      this.headerNorm = [];
      this.detectionStats = { opScore: [], assetScore: [], amountScore: [], usdScore: [] };
      this.selectedCols = { operation: -1, asset: -1, amount: -1, usd: -1 };
      this.analysis = null;
      this.assetFilter = "";
      this.sortKey = "buyUsd";
      this.sortDir = "desc";
      this.pushIntoSpreadsheet([[]]);

    },

    async handleFile(file) {
      if (this.isAnalyzing) {
        this.errorMessage = "Please wait — currently analyzing.";
        return;
      }

      this.errorMessage = "";
      this.analysis = null;
      this.filename = "";

      if (!isCsvLikeFile(file)) {
        this.errorMessage = "Only plain-text CSV files are allowed.";
        return;
      }
      if (file.size > this.MAX_BYTES) {
        this.errorMessage = `File too large. Max ${Math.round(this.MAX_BYTES / (1024 * 1024))} MB.`;
        return;
      }
      if (file.name && file.name.startsWith(".")) {
        this.errorMessage = "Hidden/system files are not allowed.";
        return;
      }

      const sample = await file.slice(0, 2048).text();
      if (isLikelyBinary(sample)) {
        this.errorMessage = "The file appears to be binary. Please provide a plain-text CSV.";
        return;
      }

      this.filename = file.name || "upload.csv";

      Papa.parse(file, {
        header: false,
        dynamicTyping: false,
        worker: false,
        skipEmptyLines: "greedy",
        complete: (res) => {
          if (res?.errors?.length) {
            this.errorMessage = `CSV parse error: ${res.errors[0].message}`;
            return;
          }
          let rows = Array.isArray(res.data) ? res.data : [];
          if (rows.length > this.MAX_ROWS) rows = rows.slice(0, this.MAX_ROWS);

          // clamp col count
          let maxCols = 0;
          for (const r of rows) maxCols = Math.max(maxCols, Array.isArray(r) ? r.length : 0);
          maxCols = Math.min(this.MAX_COLS, maxCols);

          rows = rows.map(r => (Array.isArray(r) ? r.slice(0, maxCols) : []));
          rows = rows.map(r => r.map(v => sanitizeCell(v)));

          this.rawRows = rows;

          // Find header row + detect columns
          const hdrIdx = findHeaderRow(rows);
          this.headerRowIdx = hdrIdx;
          this.headerRaw = (rows[hdrIdx] || []).map(h => String(h ?? ""));
          this.headerNorm = this.headerRaw.map(h => normalizeHeader(h));

          const auto = detectColumns(rows, hdrIdx, this.headerNorm);
          this.selectedCols = {
            operation: auto.operation,
            asset: auto.asset,
            amount: auto.amount,
            usd: auto.usd,
          };
          this.detectionStats = auto._stats;

          // Auto-run analysis
          this.analyze(rows);
        },
        error: (err) => {
          this.errorMessage = `Parse failed: ${String(err?.message || err)}`;
        },
      });
    },

    prettyHeader(h, i) {
      const s = String(h ?? "").trim();
      return s ? `C${i+1}: ${s}` : `C${i+1}: (blank)`;
    },
    colName(idx) {
      if (idx == null || idx < 0) return "—";
      return this.headerRaw?.[idx] ? String(this.headerRaw[idx]) : `C${idx+1}`;
    },

    fmtQty(v) {
      const n = Number(v ?? 0);
      if (!Number.isFinite(n)) return "—";
      // show up to 8 decimals but trim trailing zeros nicely
      return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
    },
    fmtUsd(v, allowNull = false) {
      if (v == null && allowNull) return "—";
      const n = Number(v ?? 0);
      if (!Number.isFinite(n)) return "—";
      return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
    },

analyze(rowsOverride = null) {
  let skippedMissingAsset = 0;
  let skippedBadAmount = 0;
  let skippedZeroAmount = 0;
  let skippedUnknownDir = 0;

  const skipSamples = { missingAsset: [], badAmount: [], zeroAmount: [], unknownDir: [] };
  const pushSample = (arr, obj) => { if (arr.length < 25) arr.push(obj); };

  this.isAnalyzing = true;
  this.errorMessage = "";

  try {
    // 1) Pick data source
    let rows = null;

    if (Array.isArray(rowsOverride) && rowsOverride.length) {
      rows = rowsOverride;
    } else {
      const sheetRows = this.prepareFromSheet();
      if (sheetRows === null) {
        this.errorMessage = "Spreadsheet not ready.";
        this.analysis = null;
        return;
      }
      rows = (sheetRows && sheetRows.length) ? sheetRows : this.rawRows;
    }

    rows = trimEmptyEdges(rows || []);

    if (!rows.length || !rows[0]?.length) {
      this.errorMessage = "No data to analyze.";
      this.analysis = null;
      return;
    }

    // 2) Header detection (now works for sheet OR upload)
    const hdrIdx = findHeaderRow(rows);
    this.headerRowIdx = hdrIdx;
    this.headerRaw = (rows[hdrIdx] || []).map(h => String(h ?? ""));
    this.headerNorm = this.headerRaw.map(h => normalizeHeader(h));
const ccAuto = detectCryptoComCols(this.headerNorm);
const cryptoComActive = isCryptoComFile(ccAuto);

// If crypto.com is active, we should prefer the known USD column even if detectColumns misses it
// (only when user hasn't manually overridden USD/Operation)
const auto = detectColumns(rows, hdrIdx, this.headerNorm);
this.detectionStats = auto._stats;

const cols = {
  operation: this.selectedCols.operation >= 0 ? this.selectedCols.operation : auto.operation,
  asset:     this.selectedCols.asset >= 0 ? this.selectedCols.asset : auto.asset,
  amount:    this.selectedCols.amount >= 0 ? this.selectedCols.amount : auto.amount,
  usd:       this.selectedCols.usd >= 0 ? this.selectedCols.usd : auto.usd,
};

// Force crypto.com op/usd if user didn't override
if (cryptoComActive) {
  if (this.selectedCols.operation < 0 && ccAuto.txnType >= 0) cols.operation = ccAuto.txnType;
  if (this.selectedCols.usd < 0 && ccAuto.nativeUsd >= 0) cols.usd = ccAuto.nativeUsd;
}

if (!cryptoComActive) {
  if (cols.asset < 0 || cols.amount < 0) {
    this.errorMessage =
      "Couldn’t confidently detect Asset and Amount columns. Open Advanced → Choose columns and set Asset + Amount.";
    this.analysis = null;
    return;
  }
} else {
  // crypto.com requires the leg columns
  if (!isCryptoComFile(ccAuto)) {
    this.errorMessage =
      "Crypto.com file detected partially, but missing required columns (Currency/Amount/To Currency/To Amount/Native Amount in USD).";
    this.analysis = null;
    return;
  }
}
const start = hdrIdx + 1;
const end = rows.length;

const perAsset = new Map();
const extractedRows = [];

let rowsUsed = 0;
let rowsSkipped = 0;
let unknownOperationRows = 0;
let usdMissingRows = 0;

const useUsd = cols.usd >= 0;

// crypto.com filter auto mode needs to know if commit rows exist
let ccFlags = { hasCommit: false, hasLockUnlock: false };
if (cryptoComActive && ccAuto.kind >= 0) {
  ccFlags = scanCryptoComKindFlags(rows, start, ccAuto.kind);
}

let skippedFilteredKind = 0;
let skippedNonTrade = 0;
let skippedMissingLeg = 0;

for (let r = start; r < end; r++) {
  const row = rows[r] || [];

  // ---- Extract effective fields (either crypto.com derived or generic) ----
  let asset = "";
  let amt = null;
  let amtRaw = "";
  let usdRaw = "";
  let usdVal = null;
  let op = null;        // buy/sell for operation mode
  let note = "";

  if (cryptoComActive) {
    const ccUse = {
      ...ccAuto,
      txnType: cols.operation >= 0 ? cols.operation : ccAuto.txnType,
      nativeUsd: cols.usd >= 0 ? cols.usd : ccAuto.nativeUsd,
    };

    const d = deriveCryptoComTradeRow(row, ccUse, {
      filterMode: this.cryptoComFilterMode,
      hasCommit: ccFlags.hasCommit,
    });

    if (d.skip) {
      rowsSkipped++;
      if (d.skipCode === "filteredKind") skippedFilteredKind++;
      else if (d.skipCode === "nonTrade") skippedNonTrade++;
      else if (d.skipCode === "missingLeg") skippedMissingLeg++;
      continue;
    }

    asset = d.asset;
    amt = d.amount;          // signed token amount
    amtRaw = d.amountRaw;
    usdRaw = d.usdRaw;
    usdVal = d.usd;
    op = d.operation;
    note = d.note || "";
  } else {
    const assetRaw = row[cols.asset];
    asset = String(assetRaw ?? "").trim().toUpperCase();

    amtRaw = row[cols.amount];
    amt = parseNumber(amtRaw);

    usdRaw = useUsd ? row[cols.usd] : null;
    usdVal = useUsd ? parseNumber(usdRaw) : null;

    if (cols.operation >= 0) op = parseOperation(row[cols.operation]);
  }

  // ---- Standard skip checks ----
  if (!asset) {
    rowsSkipped++;
    skippedMissingAsset++;
    pushSample(skipSamples.missingAsset, {
      rowIndex: r,
      opRaw: cols.operation >= 0 ? row[cols.operation] : "",
      assetRaw: cryptoComActive ? "(derived)" : (row[cols.asset] ?? ""),
      amtRaw,
      usdRaw: useUsd ? usdRaw : ""
    });
    continue;
  }

  if (amt == null) {
    rowsSkipped++;
    skippedBadAmount++;
    pushSample(skipSamples.badAmount, {
      rowIndex: r,
      opRaw: cols.operation >= 0 ? row[cols.operation] : "",
      asset,
      amtRaw,
      usdRaw: useUsd ? usdRaw : ""
    });
    continue;
  }

  if (this.ignoreZeroAmounts && amt === 0) {
    rowsSkipped++;
    skippedZeroAmount++;
    pushSample(skipSamples.zeroAmount, { rowIndex: r, asset, amtRaw });
    continue;
  }

  // ---- Determine direction ----
  let dir = null;
  if (this.netMode === "signed_amount") {
    if (amt > 0) dir = "buy";
    else if (amt < 0) dir = "sell";
  } else if (this.netMode === "operation") {
    dir = op;
  } else if (this.netMode === "operation_with_fallback") {
    dir = op;
    if (!dir) {
      if (amt > 0) dir = "buy";
      else if (amt < 0) dir = "sell";
    }
  }

  if (!dir) {
    rowsSkipped++;
    unknownOperationRows++;
    skippedUnknownDir++;
    pushSample(skipSamples.unknownDir, {
      rowIndex: r,
      opRaw: cols.operation >= 0 ? row[cols.operation] : "",
      asset,
      amtRaw,
      usdRaw: useUsd ? usdRaw : ""
    });
    continue;
  }

  // ---- Update aggregates ----
  const rec = perAsset.get(asset) || {
    asset,
    buyQty: 0, buyUsd: 0, buyCount: 0,
    sellQty: 0, sellUsd: 0, sellCount: 0,
    netQty: 0, netUsd: 0,
    avgBuyPrice: null, avgSellPrice: null,
  };

  let buyQtyAdd = 0, sellQtyAdd = 0, netQtyAdd = 0;

  if (this.netMode === "signed_amount") {
    netQtyAdd = amt; // signed
    if (amt > 0) buyQtyAdd = amt;
    else if (amt < 0) sellQtyAdd = Math.abs(amt);
  } else {
    const q = Math.abs(amt);
    if (dir === "buy") { buyQtyAdd = q; netQtyAdd = q; }
    else if (dir === "sell") { sellQtyAdd = q; netQtyAdd = -q; }
  }

  rec.buyQty += buyQtyAdd;
  rec.sellQty += sellQtyAdd;
  rec.netQty += netQtyAdd;

  if (dir === "buy") rec.buyCount += 1;
  if (dir === "sell") rec.sellCount += 1;

  // ✅ FIXED USD logic for signed_amount: sign USD based on amt sign (USD often always positive)
  if (useUsd) {
    if (usdVal == null) {
      usdMissingRows++;
    } else {
      const usdAbs = Math.abs(usdVal);

      if (this.netMode === "signed_amount") {
        rec.netUsd += (amt > 0 ? usdAbs : -usdAbs);
        if (amt > 0) rec.buyUsd += usdAbs;
        else if (amt < 0) rec.sellUsd += usdAbs;
      } else {
        if (dir === "buy") { rec.buyUsd += usdAbs; rec.netUsd += usdAbs; }
        else if (dir === "sell") { rec.sellUsd += usdAbs; rec.netUsd -= usdAbs; }
      }
    }
  }

  perAsset.set(asset, rec);
  rowsUsed++;

  extractedRows.push({
    rowIndex: r,
    operation: dir,
    asset,
    amountRaw: String(amtRaw ?? ""),
    usdRaw: useUsd ? String(usdRaw ?? "") : "",
    note: note || "",
  });
}


        // Finalize derived metrics
        const arr = Array.from(perAsset.values()).map(r => {
          const avgBuy = (useUsd && r.buyQty > 0 && r.buyUsd > 0) ? (r.buyUsd / r.buyQty) : null;
          const avgSell = (useUsd && r.sellQty > 0 && r.sellUsd > 0) ? (r.sellUsd / r.sellQty) : null;
          return {
            ...r,
            avgBuyPrice: avgBuy,
            avgSellPrice: avgSell,
          };
        });

        // Totals
        const totals = arr.reduce((t, r) => {
          t.buyQty += r.buyQty;
          t.buyUsd += r.buyUsd;
          t.sellQty += r.sellQty;
          t.sellUsd += r.sellUsd;
          t.netQty += r.netQty;
          t.netUsd += r.netUsd;
          t.buyCount += r.buyCount;
          t.sellCount += r.sellCount;
          return t;
        }, { buyQty: 0, buyUsd: 0, sellQty: 0, sellUsd: 0, netQty: 0, netUsd: 0, buyCount: 0, sellCount: 0 });

        this.analysis = {
          filename: this.filename,
          rowsScanned: (end - start),
          rowsUsed,
          rowsSkipped,
          unknownOperationRows,
          usdMissingRows: useUsd ? usdMissingRows : 0,
          columns: cols,
          perAsset: arr,
          totals,
          extractedRows,
  profile: cryptoComActive ? "cryptocom" : "generic",
  cryptoCom: cryptoComActive ? {
    filterMode: this.cryptoComFilterMode,
    hasCommitRows: ccFlags.hasCommit,
    hasLockUnlockRows: ccFlags.hasLockUnlock,
  } : null,

  skipBreakdown: {
    missingAsset: skippedMissingAsset,
    invalidAmount: skippedBadAmount,
    zeroAmount: skippedZeroAmount,
    unknownDir: skippedUnknownDir,

    // crypto.com extras
    filteredKind: skippedFilteredKind,
    nonTradeType: skippedNonTrade,
    missingLeg: skippedMissingLeg,
  },
  skipSamples,
        };
      } finally {
        this.isAnalyzing = false;
      }
    },

    downloadSummaryCsv() {
      if (!this.analysis) return;
      const useUsd = this.analysis.columns.usd >= 0;

      const headers = [
        "Asset",
        "Buy Qty",
        ...(useUsd ? ["Buy USD"] : []),
        "Buy Count",
        ...(useUsd ? ["Avg Buy Price (USD/unit)"] : []),
        "Sell Qty",
        ...(useUsd ? ["Sell USD"] : []),
        "Sell Count",
        ...(useUsd ? ["Avg Sell Price (USD/unit)"] : []),
        "Net Qty",
        ...(useUsd ? ["Net USD"] : []),
      ];

      const rows = this.analysis.perAsset.map(a => {
        const out = [
          a.asset,
          a.buyQty,
          ...(useUsd ? [a.buyUsd] : []),
          a.buyCount,
          ...(useUsd ? [a.avgBuyPrice ?? ""] : []),
          a.sellQty,
          ...(useUsd ? [a.sellUsd] : []),
          a.sellCount,
          ...(useUsd ? [a.avgSellPrice ?? ""] : []),
          a.netQty,
          ...(useUsd ? [a.netUsd] : []),
        ];
        return out;
      });

      const fname = (this.analysis.filename || "summary.csv").replace(/\.csv$/i, "") + "_asset_summary.csv";
      downloadCsv(fname, headers, rows);
    },

    downloadExtractedRowsCsv() {
      if (!this.analysis) return;
      const useUsd = this.analysis.columns.usd >= 0;

      const headers = ["RowIndex(1-based)", "Operation", "Asset", "AmountRaw", ...(useUsd ? ["UsdRaw"] : []), "Note"];
      const rows = this.analysis.extractedRows.map(r => ([
        (r.rowIndex + 1),
        r.operation ?? "",
        r.asset ?? "",
        r.amountRaw ?? "",
        ...(useUsd ? [r.usdRaw ?? ""] : []),
        r.note ?? "",
      ]));

      const fname = (this.analysis.filename || "rows.csv").replace(/\.csv$/i, "") + "_extracted_rows.csv";
      downloadCsv(fname, headers, rows);
    },
  },
};
</script>

<style scoped>
/* Shell */
.csv-page-shell {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  background:
    radial-gradient(1200px 600px at 15% -10%, rgba(43,91,215,.12), transparent 70%),
    radial-gradient(900px 500px at 85% 10%, rgba(49,209,126,.10), transparent 70%),
    #f7f9ff;
}
.center-card {
  width: min(1100px, 96vw);
  background: linear-gradient(180deg, rgba(255,255,255,.85), rgba(255,255,255,.92));
  backdrop-filter: blur(3px);
  border: 1px solid rgba(23, 43, 77, 0.08);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.08);
  padding: 20px 20px 28px;
}
.head { margin-bottom: 10px; }
.muted { opacity: 0.7; font-size: 0.9rem; }

/* Dropzone */
.panel { margin-bottom: 16px; }
.dropzone {
  position: relative;
  border: 2px dashed #87a1ff;
  border-radius: 12px;
  padding: 24px;
  min-height: 160px;
  display: flex; align-items: center; justify-content: center;
  text-align: center; user-select: none; background: #fafbff;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
}
.dropzone--over { border-color: #2b5bd7; background: rgba(43, 91, 215, 0.06); box-shadow: 0 6px 14px rgba(43, 91, 215, 0.18); }
.dropzone--invalid { border-color: #ff5d5d; background: #ffecec; box-shadow: 0 6px 14px rgba(255, 93, 93, 0.25); }
.dz-default { pointer-events: none; transition: opacity 120ms ease; }
.dropzone--over .dz-default { opacity: 0.35; }
.dz-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 120ms ease; }
.dropzone--over .dz-overlay { opacity: 1; }
.dz-callout { font-size: 1.05rem; font-weight: 600; margin: 0; }
.dz-invalid { font-weight: 500; color: #b10000; }

/* Actions */
.actions { display: flex; align-items: center; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
.primary {
  appearance: none; border: none; border-radius: 10px; padding: 10px 14px;
  background: #2b5bd7; color: white; font-weight: 600; cursor: pointer;
  box-shadow: 0 4px 12px rgba(43,91,215,.22);
}
.primary:disabled { opacity: .6; cursor: not-allowed; }
.ghost {
  appearance: none; border: 1px solid rgba(23,43,77,.15); background: #fff;
  border-radius: 10px; padding: 9px 12px; cursor: pointer;
}
.inline { display: inline-flex; align-items: center; gap: 6px; }

/* Lite panel */
.panel.lite { border: 1px dashed rgba(23, 43, 77, 0.12); border-radius: 12px; padding: 14px; }
.panel-title { margin: 0 0 8px; }

/* Grid */
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field { display: grid; gap: 6px; margin-bottom: 10px; }
select { padding: 6px 8px; border: 1px solid rgba(23,43,77,.2); border-radius: 8px; }

/* Results */
.section h2 { margin-top: 4px; }
.analysis { margin-top: 18px; }
.analysis__head { margin-bottom: 10px; }
.results {
  width: 100%; border-collapse: collapse; background: #fff;
  border: 1px solid rgba(23, 43, 77, 0.08); border-radius: 10px; overflow: hidden;
}
.results th, .results td { padding: 8px 10px; border-bottom: 1px solid rgba(23,43,77,0.06); }
.results thead th { background: #f4f6ff; text-align: left; }
.results .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; }
.results.mini th, .results.mini td { padding: 6px 8px; }
.right { text-align: right; }
.empty { padding: 10px 12px; border-left: 4px solid #ffb74d; background: #fff7e6; border-radius: 6px; }
.err { color: #b10000; font-size: .95rem; }

/* Buttons */
.file-btn {
  display: inline-block; padding: 8px 14px; border-radius: 8px;
  background: #2b5bd7; color: #fff; cursor: pointer; margin-top: 6px;
}

.advanced-row {
  margin-top: 8px;
  border-top: 1px dashed rgba(23,43,77,.12);
  padding-top: 10px;
}


/* add to this component’s <style scoped> or your global css */
.dz-default .file-btn,
.dz-default .file-btn input {
  pointer-events: auto;
}


/* visually hidden but still actionable by label clicks on every browser */
#csvInput {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.badge { padding: 0 6px; border-radius: 10px; font-size: 11px; margin-left: 6px; background: var(--surface-2, #f2f2f2); }
.chip.chip--ghost { border: 1px solid rgba(0,0,0,.12); border-radius: 10px; padding: 0 6px; font-size: 11px; margin-left: 6px; }
.token-line { margin-bottom: 4px; }

.summary-grid { margin-top: 8px; }
.summary-row { display:flex; justify-content:space-between; margin:6px 0; }
.summary-row .label { color: var(--muted-fg, #777); }
.summary-row .value { text-align:right; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.50);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* above page chrome */
}

.modal {
  width: min(560px, 92vw);
  max-height: 86vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(16,24,40,.24), 0 2px 8px rgba(16,24,40,.12);
  border: 1px solid rgba(23,43,77,.08);
}

.modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid rgba(23,43,77,.08);
}

.modal__body { padding: 16px; }
.modal__actions {
  display:flex; align-items:center; justify-content:flex-end; gap:10px;
  padding: 12px 16px; border-top: 1px solid rgba(23,43,77,.08);
}

.close {
  appearance:none; border:none; background:transparent; font-size:20px; cursor:pointer;
}

.submitting .spinner.md { margin: 6px auto 10px; }
/* Generic button base for this component (works for <button> and <router-link>) */
.btn,
.btn:visited {
  display: inline-block;
  appearance: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  line-height: 1.1;
}

/* Primary (blue) */
.btn--primary {
  background: #2b5bd7;
  color: #fff;
  border: 1px solid rgba(0,0,0,0);
}
.btn--primary:hover { filter: brightness(0.95); }
.btn--primary:focus { outline: 2px solid rgba(43,91,215,.35); outline-offset: 2px; }

/* Ghost (white with border) */
.btn--ghost {
  background: #fff;
  color: #1f2a44;
  border: 1px solid rgba(23,43,77,.15);
}
.btn--ghost:hover { background: #f5f7ff; color: #1f2a44; }
.btn--ghost:focus { outline: 2px solid rgba(43,91,215,.25); outline-offset: 2px; }

/* Disabled state */
.btn[disabled],
.btn--primary[disabled],
.btn--ghost[disabled] {
  opacity: .6;
  cursor: not-allowed;
}
/* Bulk bar layout */
.bulkbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  margin: 10px 0;
  background: #f6f8ff;
  border: 1px solid rgba(23,43,77,.10);
  border-radius: 10px;
  flex-wrap: wrap; /* allow wrap on small screens */
}

.bulkbar__left,
.bulkbar__right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn--sm { padding: 6px 10px; font-size: 0.9rem; }

/* Hide the trailing total rows on very small screens */
@media (max-width: 640px) {
  .hide-sm { display: none; }
  .bulkbar { padding: 10px; }
  .bulkbar__right { width: 100%; justify-content: space-between; }
}
/* Runner panel */
.runner {
  margin-top: 16px;
  border: 1px solid rgba(23,43,77,.10);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.runner__head {
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom: 8px;
}
.runner__controls { display:flex; align-items:center; gap:8px; }

.runner__belt {
  position: relative;
  overflow: hidden;
  border: 1px dashed rgba(23,43,77,.18);
  border-radius: 10px;
  padding: 10px;
  background: #fbfcff;
}
.runner__belt-bg {
  position:absolute; inset:0;
  background-size: 30px 30px;
  background-image:
    linear-gradient(135deg, rgba(43,91,215,.10) 25%, transparent 25%, transparent 50%, rgba(43,91,215,.10) 50%, rgba(43,91,215,.10) 75%, transparent 75%, transparent);
  animation: beltMove 1.2s linear infinite;
  opacity: .35;
  pointer-events: none;
}
@keyframes beltMove {
  from { background-position: 0 0; }
  to   { background-position: 30px 30px; }
}

.runner__boxes {
  position: relative;
  display: flex; gap: 10px;
  overflow-x: auto; padding: 2px;
}

.runner__box {
  min-width: 160px;
  background: #fff;
  border: 1px solid rgba(23,43,77,.12);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(16,24,40,.06);
}
.runner__box.is-active {
  border-color: #2b5bd7;
  box-shadow: 0 6px 16px rgba(43,91,215,.16);
}
.runner__badge {
  display:inline-block;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f1f4ff;
  color: #2b5bd7;
  margin-bottom: 6px;
}
.runner__phase { font-weight: 600; margin-bottom: 4px; }

.runner__box.phase--queued .runner__phase { color: #64748b; }
.runner__box.phase--resolving .runner__phase { color: #8b5cf6; }
.runner__box.phase--checking .runner__phase { color: #0ea5e9; }
.runner__box.phase--submitting .runner__phase { color: #2b5bd7; }
.runner__box.phase--done .runner__phase { color: #16a34a; }
.runner__box.phase--failed .runner__phase,
.runner__box.phase--skipped .runner__phase { color: #dc2626; }

.runner__progress {
  height: 8px;
  background: #eef2ff;
  border-radius: 999px;
  margin: 10px 0 4px;
  overflow: hidden;
}
.runner__bar {
  height: 100%;
  background: #2b5bd7;
  width: 0;
  transition: width 180ms ease;
}

.runner__log {
  margin-top: 6px;
}
.runner__log summary {
  cursor: pointer;
  font-weight: 600;
}
.runner__log ul { margin: 6px 0 0; padding-left: 18px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
/* Runner modal sizing */
.modal--runner {
  width: min(980px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(16,24,40,.24), 0 2px 8px rgba(16,24,40,.12);
  border: 1px solid rgba(23,43,77,.08);
}

.runner__head-actions {
  display:flex; align-items:center; gap:8px;
}

/* State banners */
.runner__banner {
  padding: 10px 12px;
  margin: 10px 16px 0;
  border-radius: 10px;
  font-weight: 600;
}
.runner__banner--paused {
  background: #fff3cd;
  color: #7a5d00;
  border: 1px solid #ffe08a;
}
.runner__banner--cancelled {
  background: #fdecea;
  color: #8f1d1d;
  border: 1px solid #f5c2c0;
}
.runner__banner--done {
  background: #e9f7ef;
  color: #0f6d2a;
  border: 1px solid #bde5c8;
}

/* Belt + background animation control */
.runner__belt {
  position: relative;
  overflow: hidden;
  border: 1px dashed rgba(23,43,77,.18);
  border-radius: 10px;
  padding: 10px;
  background: #fbfcff;
  margin: 12px 16px;
}

.runner__belt-bg {
  position:absolute; inset:0;
  background-size: 30px 30px;
  background-image:
    linear-gradient(135deg, rgba(43,91,215,.10) 25%, transparent 25%, transparent 50%, rgba(43,91,215,.10) 50%, rgba(43,91,215,.10) 75%, transparent 75%, transparent);
  animation: beltMove 1.2s linear infinite;
  opacity: .35;
  pointer-events: none;
}
@keyframes beltMove {
  from { background-position: 0 0; }
  to   { background-position: 30px 30px; }
}

/* Pause/stop the belt subtly */
.runner__belt.is-paused .runner__belt-bg { animation-play-state: paused; filter: grayscale(1); opacity: .22; }
.runner__belt.is-stopped .runner__belt-bg { animation-play-state: paused; opacity: .12; }

/* Grid of boxes with vertical scroll for large batches */
.runner__grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-auto-rows: auto;
  gap: 10px;
  max-height: 360px; /* keep modal compact */
  overflow: auto;
  padding: 2px;
}

/* Job cards */
.runner__box {
  min-width: 0;
  background: #fff;
  border: 1px solid rgba(23,43,77,.12);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(16,24,40,.06);
}
.runner__box.is-active {
  border-color: #2b5bd7;
  box-shadow: 0 6px 16px rgba(43,91,215,.16);
}
.runner__badge {
  display:inline-block;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f1f4ff;
  color: #2b5bd7;
  margin-bottom: 6px;
}
.runner__phase { font-weight: 600; margin-bottom: 4px; }

.runner__box.phase--queued .runner__phase { color: #64748b; }
.runner__box.phase--resolving .runner__phase { color: #8b5cf6; }
.runner__box.phase--checking .runner__phase { color: #0ea5e9; }
.runner__box.phase--submitting .runner__phase { color: #2b5bd7; }
.runner__box.phase--done .runner__phase { color: #16a34a; }
.runner__box.phase--failed .runner__phase,
.runner__box.phase--skipped .runner__phase { color: #dc2626; }

.runner__meta { padding: 0 16px 6px; }

.runner__progress {
  height: 8px;
  background: #eef2ff;
  border-radius: 999px;
  margin: 8px 0 4px;
  overflow: hidden;
}
.runner__bar {
  height: 100%;
  background: #2b5bd7;
  width: 0;
  transition: width 180ms ease;
}

.runner__counts { text-align: right; }

.runner__log { margin: 6px 16px 12px; }
.runner__log summary { cursor: pointer; font-weight: 600; }
.runner__log ul { margin: 6px 0 0; padding-left: 18px; }

.btn--sm { padding: 6px 10px; font-size: 0.9rem; }



.report__disclaimer {
user-select: none;
}
.disclaimer__summary strong { font-weight: 700; }
.disclaimer__version { color: #6b7280; font-size: 12px; }
.disclaimer__hint { margin-left: auto; color: #9ca3af; font-size: 12px; }


/* Card */
.disclaimer__card {
margin-top: 8px;
background: #f8fafc;
border: 1px solid #e5e7eb;
border-radius: 8px;
padding: 12px 12px 8px;
max-height: 180px; /* fits in modal without crowding */
overflow: auto; /* scroll if long */
position: relative;
}


/* subtle top/bottom fade for scrollables */
.disclaimer__card::after {
content: '';
position: sticky;
bottom: -1px;
left: 0; right: 0;
height: 18px;
background: linear-gradient(to bottom, rgba(248,250,252,0), rgba(248,250,252,1));
pointer-events: none;
}


.disclaimer__list {
margin: 8px 0 0 18px;
padding: 0;
line-height: 1.35;
font-size: 13px;
color: #111827;
}
.disclaimer__list li { margin-bottom: 6px; }
.muted { color: #6b7280; font-size: 12px; }


/* Acknowledgement row */
.disclaimer__ack {
display: flex;
align-items: center;
gap: 10px;
margin-top: 10px;
}
.checkbox { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; }


/* Print: always show full disclaimer, no scroll */
@media print {
.disclaimer__card { max-height: none; overflow: visible; background: transparent; border-color: #ddd; }
.disclaimer__summary .disclaimer__hint { display: none; }
}
.advanced-actions { display:flex; gap:.5rem; flex-wrap:wrap; margin:.75rem 0; }

/* Report modal tweaks */
.modal--report { max-width: 760px; }
.report__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .4rem .8rem;
  padding: .2rem 0 .6rem;
  border-bottom: 1px solid var(--border);
}
.report__meta .meta__row { display: contents; }
.report__meta .label { color: var(--muted); font-size: .9rem; }
.report__meta .value { font-weight: 600; min-width: 0; }
.report__meta .truncate { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }

.report__body { padding: .8rem 0 .4rem; }
.report__text {
  white-space: pre-wrap;
  line-height: 1.5;
  margin: 0;
}

.report__actions {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: .6rem;
}
/* Full‑screen overlay; does not alter body scrolling */
.report-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.45);
  display: grid;
  place-items: start center;    /* top-ish; change to center if you prefer */
  padding: 24px 16px;
}

/* Your modal content scrolls internally if tall */
.modal--report {
  max-width: 760px;
  width: calc(100% - 32px);
  max-height: min(85vh, 900px);
  overflow: auto;               /* only the modal scrolls, page remains as-is */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}

.copy-btn {
  position: relative;
  /* Keep width steady so label swap doesn’t jiggle layout */
  min-width: 104px; /* adjust to your font/UI */
}

.copy-btn .copy-label-default,
.copy-btn .copy-label-done {
  display: inline-block;
  transition: opacity 140ms ease;
}

.copy-btn .copy-label-done {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.copy-btn.is-copied .copy-label-default { opacity: 0; }
.copy-btn.is-copied .copy-label-done   { opacity: 1; }

/* Optional: subtle success ring */
.copy-btn.is-copied {
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
  animation: copyPulse 600ms ease;
}

@keyframes copyPulse {
  0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70%  { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* Disabled state stays readable */
.copy-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}
/* Distinct action styles */
.btn--image {
  background: #eef6ff;
  border-color: #cfe0fb;
  color: #0b63a9;
}
.btn--image:hover { filter: none; background:#e6f1ff; }

.btn--bug {
  background: #fff6f0;
  border-color: #f7d2bd;
  color: #7a3b00;
}
.btn--bug:hover { filter: none; background:#ffeede; }

/* Legend */
.flags { list-style: none; padding: 0; margin: .25rem 0 0; display: grid; gap: .4rem; }
.flag { width: 18px; vertical-align: middle; margin-right: 8px; }
.flag { width: 18px; height: 18px; margin-top: 2px; }

/* Table layout */
.results-wrap {
  position: relative;
  overflow-x: auto;
}
.results {
  width: 100%;
  table-layout: fixed; /* enables predictable truncation */
}

/* Monospace and clip utilities */
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace; }
.cell-clip { max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cell-clip .truncate { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }

/* Tiny inline copy button only appears when hovering the cell */
.copychip {
  margin-left: 8px;
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Right sticky columns */
:root {
  --col-status: 120px;
  --col-send:   110px;
  --col-resolve:130px;
  --sticky-bg: var(--card-bg, #fff);
}

th.sticky-status, td.sticky-status {
  position: sticky; right: 0;
  background: var(--sticky-bg);
  z-index: 3; /* above rows */
  width: var(--col-status);
}

th.sticky-send, td.sticky-send {
  position: sticky; right: var(--col-status);
  background: var(--sticky-bg);
  z-index: 3;
  width: var(--col-send);
}

th.sticky-resolve, td.sticky-resolve {
  position: sticky; right: calc(var(--col-status) + var(--col-send));
  background: var(--sticky-bg);
  z-index: 3;
  width: var(--col-resolve);
}

/* Subtle divider between sticky group and scrollable area */
td.sticky-resolve, th.sticky-resolve {
  box-shadow: -1px 0 0 rgba(0,0,0,0.06) inset;
}

/* Right scroll fade to hint there is more */
.scroll-fade-right {
  position: sticky;
  right: 0;
  top: 0;
  width: 28px;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(to left, var(--card-bg, #fff), rgba(255,255,255,0));
  z-index: 4;
}

/* Responsive tightening: hide "Type" on narrower widths if needed */
@media (max-width: 1280px) {
  .results colgroup col:nth-child(6) { width: 0 !important; }
  .results thead th:nth-child(6),
  .results tbody td:nth-child(6) { display: none; }
  .cell-clip { max-width: 220px; }
}
@media (max-width: 1024px) {
  .cell-clip { max-width: 180px; }
}
/* Keep fixed layout; we’ll enforce minimums per-column */
.results { table-layout: fixed; }

/* Prevent header wrapping/overlap and keep ellipsis if space is tight */
.results thead th {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Column minimums tuned to show ~8 chars of tx and ~6 of address (monospace) */
:root {
  /* Adjust these two if you want a bit more or less */
  --tx-min-ch: 18ch;  /* ~0x + 8–10 visible chars + breathing room */
  --to-min-ch: 16ch;  /* ~0x + 6–8 visible chars + breathing room */
}

/* Apply to header + cell for both columns */
.th-tx, .td-tx { min-width: var(--tx-min-ch); }
.th-to, .td-to { min-width: var(--to-min-ch); }

/* Ensure the text truncates predictably and the Copy chip doesn’t squeeze text */
.cell-clip {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 56px; /* reserve space so the chip never overlaps text */
}

/* The visible text line; can be omitted if you already use .truncate globally */
.cell-clip .truncate {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

/* Make the Copy chip absolute so it doesn’t change the column width */
.copychip {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Slightly tighter on smaller screens while still keeping the targets readable */
@media (max-width: 1280px) {
  :root {
    --tx-min-ch: 16ch;
    --to-min-ch: 14ch;
  }
}
@media (max-width: 1024px) {
  :root {
    --tx-min-ch: 15ch;
    --to-min-ch: 13ch;
  }
}
/* Keep fixed layout for performance and stickiness */
.results { table-layout: fixed; }

/* HEADERS: always show fully; never ellipsis */
.results thead th {
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
}

/* Tx/Dest columns should get the leftover width, but never collapse too far */
:root {
  /* Tune visible characters: bump up/down these to taste */
  --tx-ch: 24ch;  /* shows ~0x + 10–12 hex comfortably */
  --to-ch: 20ch;  /* shows ~0x + 8–10 hex comfortably */
}

/* Ensure the Tx/Dest column content can breathe */
.td-tx .truncate { max-width: var(--tx-ch); }
.td-to .truncate { max-width: var(--to-ch); }

/* Clip the cell text, but keep a gutter so the Copy chip never overlaps */
.cell-clip {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 56px; /* space for Copy chip */
}

/* Copy chip absolutely positioned, so it doesn't squeeze column width */
.copychip {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Slightly tighter on narrower screens */
@media (max-width: 1280px) {
  :root { --tx-ch: 22ch; --to-ch: 18ch; }
  /* You can also shave a bit more here if needed */
}
@media (max-width: 1024px) {
  :root { --tx-ch: 20ch; --to-ch: 16ch; }
}

</style>

<style>
.route-tool #app > nav { z-index: 1000; }
.route-tool,
.route-tool #app,
.route-tool #app > main { background: #f7f9fc; }
</style>
